import React, { useState } from 'react';
import { adminAPI } from '../../services/api';
import { Trash2, AlertTriangle, Calendar, Database } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import toast from 'react-hot-toast';

const Maintenance = () => {
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(90);
  const [cleanupStatus, setCleanupStatus] = useState('completed');

  const handleCleanup = async () => {
    if (!window.confirm(
      `Are you sure you want to delete all ${cleanupStatus} orders older than ${cleanupDays} days? This action cannot be undone.`
    )) {
      return;
    }

    setCleanupLoading(true);
    try {
      const response = await adminAPI.cleanupOrders({ 
        days: cleanupDays, 
        status: cleanupStatus 
      });
      
      const result = response.data.data;
      toast.success(`Cleanup completed: ${result.deletedCount} orders removed`);
    } catch (error) {
      toast.error('Cleanup failed');
    } finally {
      setCleanupLoading(false);
    }
  };

  const maintenanceTasks = [
    {
      title: 'Database Cleanup',
      description: 'Remove old completed orders to free up database space',
      icon: Database,
      action: 'cleanup-orders',
      risk: 'medium'
    },
    {
      title: 'Log Rotation',
      description: 'Archive and compress old log files',
      icon: Calendar,
      action: 'rotate-logs',
      risk: 'low'
    },
    {
      title: 'Cache Clear',
      description: 'Clear application cache and temporary files',
      icon: Trash2,
      action: 'clear-cache',
      risk: 'low'
    }
  ];

  const getRiskBadge = (risk) => {
    const riskClasses = {
      low: 'badge-success',
      medium: 'badge-warning',
      high: 'badge-danger'
    };

    return (
      <span className={`badge ${riskClasses[risk]}`}>
        {risk.toUpperCase()} RISK
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-medium text-gray-900">System Maintenance</h2>
        <p className="mt-1 text-sm text-gray-500">
          Perform system maintenance tasks and cleanup operations
        </p>
      </div>

      {/* Warning Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
        <div className="flex">
          <AlertTriangle className="h-5 w-5 text-yellow-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Maintenance Warning
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                Maintenance operations can affect system performance and may result in data loss. 
                Always create backups before performing maintenance tasks, especially in production environments.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Order Cleanup */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center">
            <Trash2 className="h-5 w-5 mr-2" />
            Order Cleanup
          </h3>
        </div>
        <div className="card-content">
          <p className="text-sm text-gray-600 mb-4">
            Remove old orders to free up database space. This operation permanently deletes order records.
          </p>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mb-4">
            <div>
              <label className="form-label">Order Status</label>
              <select
                value={cleanupStatus}
                onChange={(e) => setCleanupStatus(e.target.value)}
                className="input"
              >
                <option value="completed">Completed Orders</option>
                <option value="failed">Failed Orders</option>
                <option value="pending">Pending Orders</option>
              </select>
            </div>
            
            <div>
              <label className="form-label">Older Than (Days)</label>
              <input
                type="number"
                min="30"
                max="365"
                value={cleanupDays}
                onChange={(e) => setCleanupDays(parseInt(e.target.value))}
                className="input"
              />
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              This will delete all {cleanupStatus} orders older than {cleanupDays} days
            </div>
            <button
              onClick={handleCleanup}
              disabled={cleanupLoading}
              className="btn btn-danger"
            >
              {cleanupLoading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Start Cleanup
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Maintenance Tasks */}
      <div className="grid gap-6 md:grid-cols-2">
        {maintenanceTasks.map((task) => {
          const Icon = task.icon;
          return (
            <div key={task.action} className="card">
              <div className="card-content">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <Icon className="h-8 w-8 text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">
                        {task.title}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {task.description}
                      </p>
                    </div>
                  </div>
                  {getRiskBadge(task.risk)}
                </div>
                
                <button
                  className="btn btn-outline w-full"
                  disabled
                >
                  Coming Soon
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* System Health Check */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">System Health Check</h3>
        </div>
        <div className="card-content">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-gray-200">
              <span className="text-sm text-gray-600">Database Connection</span>
              <span className="badge badge-success">Healthy</span>
            </div>
            
            <div className="flex items-center justify-between py-2 border-b border-gray-200">
              <span className="text-sm text-gray-600">API Endpoints</span>
              <span className="badge badge-success">Operational</span>
            </div>
            
            <div className="flex items-center justify-between py-2 border-b border-gray-200">
              <span className="text-sm text-gray-600">File System</span>
              <span className="badge badge-success">Normal</span>
            </div>
            
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-600">Memory Usage</span>
              <span className="badge badge-warning">Moderate</span>
            </div>
          </div>
          
          <div className="mt-6">
            <button className="btn btn-outline w-full">
              Run Full Health Check
            </button>
          </div>
        </div>
      </div>

      {/* Maintenance Schedule */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Recommended Maintenance Schedule</h3>
        </div>
        <div className="card-content">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-medium text-gray-900">Daily</div>
                <div className="text-sm text-gray-500">Log rotation, cache cleanup</div>
              </div>
              <span className="text-sm text-gray-400">Automated</span>
            </div>
            
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-medium text-gray-900">Weekly</div>
                <div className="text-sm text-gray-500">Database optimization, health checks</div>
              </div>
              <span className="text-sm text-gray-400">Manual</span>
            </div>
            
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-medium text-gray-900">Monthly</div>
                <div className="text-sm text-gray-500">Order cleanup, full backup</div>
              </div>
              <span className="text-sm text-gray-400">Manual</span>
            </div>
            
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-medium text-gray-900">Quarterly</div>
                <div className="text-sm text-gray-500">Security audit, performance review</div>
              </div>
              <span className="text-sm text-gray-400">Manual</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Maintenance;
